import { Box, Button, Grid, Typography } from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import DownloadIcon from "@mui/icons-material/Download";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import type { RootState } from "../../../../../store/Index";
import { useSelector } from "react-redux";
import { MemoryBook } from "../../../../../types/memory-table";
import { User } from "../../../../users-admin/types/User";
import apiService from "../../../../../service/Api";
import { useEffect } from "react";
import { toast } from "react-toastify";
import { showErrorToast } from "../../../../../components/common/ShowToast";

interface Props {
  memoryBooks: MemoryBook[];
  isLoading: boolean;
  error: boolean;
  uploadUser: User | null;
  isAdmin: boolean;
}

export const CurrentMemoryBookInfo: React.FC<Props> = ({
  memoryBooks,
  isLoading,
  error,
  uploadUser,
  isAdmin,
}) => {
  const reduxTheme = useSelector((state: RootState) => state.theme);

  const determineUploadedName = (user: User | null): string | undefined => {
    const currentBook = memoryBooks[0];

    if (
      currentBook.created_by.first_name.length === 0 ||
      currentBook.created_by.last_name.length === 0
    ) {
      return currentBook.created_by.username;
    }

    return `${currentBook.created_by.first_name} ${currentBook.created_by.last_name}`;
  };

  const downloadCurrentMemoryBook = async () => {
    try {
      const url = memoryBooks[0].file;

      fetch(url)
        .then((response) => response.blob())
        .then((blob) => {
          const a = document.createElement("a");
          a.href = URL.createObjectURL(blob);
          a.download = `memory_book_${memoryBooks[0].id}.pdf`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(a.href);
        })
        .catch((error) =>
          showErrorToast("Failed to download memory book. Please try again.")
        );

      /*  const a = document.createElement("a");
      a.href = url;
      a.download = `memory_book_${memoryBooks[0].id}.pdf`; // suggest a filename (optional)
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a); */
    } catch (err: any) {
      toast.error("Failed to download memory book.Please try again", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    }
  };

  const viewBook = () => {
    window.open(memoryBooks[0].file, "_blank", "noopener,noreferrer");
  };

  return (
    <Box padding={1} bgcolor={"#F0F4F8"} mb={2}>
      <Typography variant="body1" mb={1} fontWeight={500}>
        Current File
      </Typography>

      {!isLoading && error && (
        <Box
          display={"flex"}
          justifyContent={"center"}
          alignItems={"center"}
          flexDirection={"column"}
        >
          <DescriptionIcon
            sx={{ color: reduxTheme.primary_colors[300], mb: 2 }}
          />
          <Typography variant="body1">
            Cannot display current file at the moment
          </Typography>
        </Box>
      )}

      {!isLoading && !error && memoryBooks.length === 0 && (
        <Box
          display={"flex"}
          justifyContent={"center"}
          alignItems={"center"}
          flexDirection={"column"}
        >
          <DescriptionIcon
            sx={{ color: reduxTheme.primary_colors[300], mb: 2 }}
          />
          <Typography variant="body1">
            No memory book available at the moment
          </Typography>
        </Box>
      )}

      {!isLoading && !error && memoryBooks.length > 0 && (
        <Grid container justifyContent={"space-between"}>
          <Grid display={"flex"} alignItems={"center"}>
            <DescriptionIcon sx={{ color: reduxTheme.primary_colors[300] }} />
            <Box ml={1}>
              <Typography variant="body1" fontWeight={600}>
                {memoryBooks[0].file.split("/").at(-1)}
              </Typography>
              {isAdmin && (
                <Typography variant="body1" fontSize={10}>
                  {determineUploadedName(uploadUser)}
                </Typography>
              )}
            </Box>
          </Grid>

          <Grid>
            <Button variant="contained" onClick={downloadCurrentMemoryBook}>
              <DownloadIcon />
              Download
            </Button>

            <Button onClick={viewBook}>
              <FullscreenIcon />
              View Book
            </Button>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};
